import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { useCustomModel } from "components/context/CustomProvider";
import { useController, useForm } from "react-hook-form";
import { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CustomFileField from "components/ui/customs/forms/fields/CustomFileField";
import CustomFieldBox from "components/ui/customs/components/CustomFieldBox";

const DocumentSingleFileField = ({ field, control }) => {
  const { field: fieldProps, fieldState } = useController({
    control,
    name: field.name,
  });

  const model = useCustomModel("backoffice.DocumentFile");
  const innerField = model.fields.find((f) => f.name === "file");

  const {
    control: innerControl,
    handleSubmit: innerHandleSubmit,
    setError: innerSetError,
    clearErrors: innerClearErrors,
    watch: innerWatch,
  } = useForm({ defaultValues: fieldProps.value });
  const extValue = innerWatch("file");

  useEffect(() => {
    if (!fieldState.error) {
      innerClearErrors("file");
    } else {
      innerSetError("file", fieldState.error);
    }
  }, [fieldState.error, innerSetError]);

  useEffect(() => {
    innerHandleSubmit((v) => {
      if (!v.file) {
        fieldProps.onChange(null);
      } else if (!v.file.content) {
        fieldProps.onChange(v);
      } else {
        fieldProps.onChange({
          original_filename: v.file.name,
          file: v.file,
        });
      }
    })();
  }, [extValue]);

  if (field.disabled) {
    return (
      <TextField
        disabled
        label={field.label}
        value={fieldProps.value.original_filename || ""}
      />
    );
  }

  return (
    <CustomFileField
      field={innerField}
      control={innerControl}
      label={field.label}
    />
  );
};

const MultipleDocumentFileField = ({ field, control }) => {
  const { field: fieldProps, fieldState } = useController({
    control,
    name: field.name,
  });

  const model = useCustomModel("backoffice.DocumentFile");
  const innerField = model.fields.find((f) => f.name === "file");

  const {
    control: innerControl,
    handleSubmit: innerHandleSubmit,
    setError: innerSetError,
    clearErrors: innerClearErrors,
    watch: innerWatch,
    reset: innerReset,
  } = useForm({
    defaultValues: {
      file: null,
    },
  });
  const extValue = innerWatch("file");

  useEffect(() => {
    if (!fieldState.error) {
      innerClearErrors("file");
    } else {
      innerSetError("file", fieldState.error);
    }
  }, [fieldState.error, innerSetError]);

  useEffect(() => {
    innerHandleSubmit((v) => {
      if (!v.file) {
        return fieldProps.onChange(fieldProps.value);
      }
      const returnValue = v.file
        .filter((f) => f.content)
        .map((f) => ({
          original_filename: f.name,
          file: f,
        }));
      fieldProps.onChange([...fieldProps.value, ...returnValue]);
      innerReset();
    })();
  }, [extValue]);

  const handleRemove = (index) => {
    const newValue = [...fieldProps.value];
    newValue.splice(index, 1);
    fieldProps.onChange(newValue);
  };

  return (
    <CustomFieldBox
      label={field.label}
      helperText={field.helperText}
      error={!!fieldState.error}
    >
      {fieldProps?.value?.map((v, index) => (
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography key={index}>{v.original_filename}</Typography>
          <IconButton onClick={() => handleRemove(index)} color={"error"}>
            <CloseIcon sx={{ scale: 0.8 }} />
          </IconButton>
        </Stack>
      ))}
      <CustomFileField
        field={innerField}
        control={innerControl}
        multiple
        label="Add files"
      />
    </CustomFieldBox>
  );
};

const DocumentFileField = ({ multiple, ...props }) => {
  if (multiple) return <MultipleDocumentFileField {...props} />;
  else return <DocumentSingleFileField {...props} />;
};

export default DocumentFileField;
